/**
 * Firestore database service for trAInedup.ai
 * Handles all database operations with proper error handling and type safety
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  writeBatch,
  DocumentSnapshot,
  QueryConstraint,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS } from '../../utils/constants';
import {
  User,
  Course,
  UserProgress,
  Comment as CourseComment,
} from '../../types';

/**
 * Generic interface for paginated query results
 */
export interface PaginatedResult<T> {
  data: T[];
  lastDoc: DocumentSnapshot | null;
  hasMore: boolean;
}

/**
 * Generic interface for query options
 */
export interface QueryOptions {
  limit?: number;
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
  startAfterDoc?: DocumentSnapshot;
  filters?: Array<{
    field: string;
    operator: any;
    value: any;
  }>;
}

/**
 * User-related database operations
 */
export class UserService {
  /**
   * Get user by ID
   */
  static async getUser(userId: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.users, userId));
      return userDoc.exists() ? (userDoc.data() as User) : null;
    } catch (error) {
      throw new Error('Failed to get user data');
    }
  }

  /**
   * Update user data
   */
  static async updateUser(userId: string, userData: Partial<User>): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.users, userId), {
        ...userData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw new Error('Failed to update user data');
    }
  }

  /**
   * Get all users with pagination (admin only)
   */
  static async getUsers(options: QueryOptions = {}): Promise<PaginatedResult<User>> {
    try {
      const constraints: QueryConstraint[] = [];

      if (options.orderByField) {
        constraints.push(orderBy(options.orderByField, options.orderDirection || 'desc'));
      }

      if (options.limit) {
        constraints.push(limit(options.limit));
      }

      if (options.startAfterDoc) {
        constraints.push(startAfter(options.startAfterDoc));
      }

      if (options.filters) {
        options.filters.forEach(filter => {
          constraints.push(where(filter.field, filter.operator, filter.value));
        });
      }

      const q = query(collection(db, COLLECTIONS.users), ...constraints);
      const querySnapshot = await getDocs(q);

      const users = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        uid: doc.id,
      })) as User[];

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      const hasMore = querySnapshot.docs.length === (options.limit || 20);

      return { data: users, lastDoc, hasMore };
    } catch (error) {
      throw new Error('Failed to get users');
    }
  }
}

/**
 * Course-related database operations
 */
export class CourseService {
  /**
   * Get course by ID
   */
  static async getCourse(courseId: string): Promise<Course | null> {
    try {
      const courseDoc = await getDoc(doc(db, COLLECTIONS.courses, courseId));
      return courseDoc.exists() ? ({ ...courseDoc.data(), id: courseDoc.id } as Course) : null;
    } catch (error) {
      throw new Error('Failed to get course data');
    }
  }

  /**
   * Get all published courses
   */
  static async getCourses(options: QueryOptions = {}): Promise<PaginatedResult<Course>> {
    try {
      const constraints: QueryConstraint[] = [
        where('isPublished', '==', true),
      ];

      if (options.orderByField) {
        constraints.push(orderBy(options.orderByField, options.orderDirection || 'desc'));
      }

      if (options.limit) {
        constraints.push(limit(options.limit));
      }

      if (options.startAfterDoc) {
        constraints.push(startAfter(options.startAfterDoc));
      }

      if (options.filters) {
        options.filters.forEach(filter => {
          constraints.push(where(filter.field, filter.operator, filter.value));
        });
      }

      const q = query(collection(db, COLLECTIONS.courses), ...constraints);
      const querySnapshot = await getDocs(q);

      const courses = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as Course[];

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      const hasMore = querySnapshot.docs.length === (options.limit || 20);

      return { data: courses, lastDoc, hasMore };
    } catch (error) {
      throw new Error('Failed to get courses');
    }
  }

  /**
   * Create a new course
   */
  static async createCourse(courseData: Omit<Course, 'id'>): Promise<string> {
    try {
      const courseRef = doc(collection(db, COLLECTIONS.courses));
      await setDoc(courseRef, {
        ...courseData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return courseRef.id;
    } catch (error) {
      throw new Error('Failed to create course');
    }
  }

  /**
   * Update course
   */
  static async updateCourse(courseId: string, courseData: Partial<Course>): Promise<void> {
    try {
      await updateDoc(doc(db, COLLECTIONS.courses, courseId), {
        ...courseData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw new Error('Failed to update course');
    }
  }
}

/**
 * User progress tracking operations
 */
export class ProgressService {
  /**
   * Get user's progress for a specific course
   */
  static async getUserProgress(userId: string, courseId: string): Promise<UserProgress | null> {
    try {
      const progressDoc = await getDoc(doc(db, COLLECTIONS.userProgress, `${userId}_${courseId}`));
      return progressDoc.exists() ? (progressDoc.data() as UserProgress) : null;
    } catch (error) {
      throw new Error('Failed to get progress data');
    }
  }

  /**
   * Update user's progress for a course
   */
  static async updateUserProgress(
    userId: string,
    courseId: string,
    progressData: Partial<UserProgress>
  ): Promise<void> {
    try {
      const progressId = `${userId}_${courseId}`;
      const progressRef = doc(db, COLLECTIONS.userProgress, progressId);

      await setDoc(progressRef, {
        userId,
        courseId,
        ...progressData,
        lastAccessedAt: serverTimestamp(),
      }, { merge: true });
    } catch (error) {
      throw new Error('Failed to update progress');
    }
  }

  /**
   * Get all progress records for a user
   */
  static async getUserAllProgress(userId: string): Promise<UserProgress[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.userProgress),
        where('userId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          startedAt: data.startedAt?.toDate ? data.startedAt.toDate() : data.startedAt,
          completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : data.completedAt,
          lastAccessedAt: data.lastAccessedAt?.toDate ? data.lastAccessedAt.toDate() : data.lastAccessedAt,
        } as UserProgress;
      });
    } catch (error) {
      console.error(`Failed to get user progress for ${userId}:`, error);
      return [];
    }
  }

  /**
   * Mark lesson as completed
   */
  static async markLessonCompleted(
    userId: string,
    courseId: string,
    lessonId: string
  ): Promise<void> {
    try {
      const progressId = `${userId}_${courseId}`;
      const progressRef = doc(db, COLLECTIONS.userProgress, progressId);

      await updateDoc(progressRef, {
        completedLessons: arrayUnion(lessonId),
        lastAccessedAt: serverTimestamp(),
      });
    } catch (error) {
      throw new Error('Failed to mark lesson as completed');
    }
  }
}

/**
 * Comment system operations
 */
export class CommentService {
  /**
   * Get comments for a course
   */
  static async getCourseComments(courseId: string): Promise<CourseComment[]> {
    try {
      const q = query(
        collection(db, COLLECTIONS.comments),
        where('courseId', '==', courseId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as CourseComment[];
    } catch (error) {
      throw new Error('Failed to get comments');
    }
  }

  /**
   * Add a comment to a course
   */
  static async addComment(commentData: Omit<CourseComment, 'id'>): Promise<string> {
    try {
      const commentRef = doc(collection(db, COLLECTIONS.comments));
      await setDoc(commentRef, {
        ...commentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return commentRef.id;
    } catch (error) {
      throw new Error('Failed to add comment');
    }
  }

  /**
   * Delete a comment
   */
  static async deleteComment(commentId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.comments, commentId));
    } catch (error) {
      throw new Error('Failed to delete comment');
    }
  }
}

/**
 * Real-time subscription helper
 */
export const subscribeToDocument = <T>(
  collectionName: string,
  documentId: string,
  callback: (data: T | null) => void
): Unsubscribe => {
  return onSnapshot(
    doc(db, collectionName, documentId),
    (doc) => {
      if (doc.exists()) {
        callback({ ...doc.data(), id: doc.id } as T);
      } else {
        callback(null);
      }
    },
    (_error) => {
      callback(null);
    }
  );
};

/**
 * Real-time collection subscription helper
 */
export const subscribeToCollection = <T>(
  collectionName: string,
  constraints: QueryConstraint[],
  callback: (data: T[]) => void
): Unsubscribe => {
  const q = query(collection(db, collectionName), ...constraints);
  
  return onSnapshot(
    q,
    (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as T[];
      callback(data);
    },
    (_error) => {
      callback([]);
    }
  );
};

/**
 * Batch operations helper
 */
export const createBatch = () => {
  return writeBatch(db);
};

